import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { Container, Grid, Typography, Box, Alert, CircularProgress, Tabs, Tab, alpha, useMediaQuery, Tooltip, IconButton, Link } from '@mui/material';
import {
  Logout as LogoutIcon,
  Chat as ChatIcon,
  SportsEsports as GamesIcon,
  Lock as LockIcon,
  CloudDownload as CloudDownloadIcon,
  Storage as TrueNASIcon,
  Dns as ServerIcon,
  OpenInNew,
  Refresh
} from '@mui/icons-material';
import {
  BarChart3,
  Folder,
  Briefcase,
  Users,
  Library,
  Clock,
  Info as InfoIcon,
  Settings,
  Grid3X3,
  Cpu,
  HardDrive,
  Shield,
  Gamepad2,
  LayoutDashboard,
  PencilRuler,
  Zap,
  DatabaseBackup,
  Home,
  Activity
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import modern components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Documentation from './components/Documentation';
import StatusCard from './components/StatusCard';
import ServiceCard from './components/ServiceCard';
import CronJobCard from './components/CronJobCard';
import AppsManagement from './components/AppsManagement';
import VirtualMachines from './components/VirtualMachines';
import StoragePools from './components/StoragePools';
import DataProtection from './components/DataProtection';
import SecuritySettings from './components/SecuritySettings';
import Maintenance from './components/Maintenance';
import ImmichStatistics from './components/ImmichStatistics';
import ImmichAbout from './components/ImmichAbout';
import ImmichJobs from './components/ImmichJobs';
import ImmichUsers from './components/ImmichUsers';
import ImmichLibraries from './components/ImmichLibraries';
import ImmichNightlyTasks from './components/ImmichNightlyTasks';
import JellyfinStatistics from './components/JellyfinStatistics';
import TaskScheduler from './components/TaskScheduler';
import ErrorBoundary from './components/ErrorBoundary';
import LoginForm from './Login';
import TrueNASOfflineCard from './components/TrueNASOfflineCard';
import AnimatedBackground from './components/AnimatedBackground';
import Chat from './components/Chat';
import ServerPingDefender from './components/ServerPingDefender';
import DiskWhackAMole from './components/DiskWhackAMole';
import SnakeGame from './components/SnakeGame';
import FileExplorer from './components/FileExplorer';
import NASDrive from './components/NASDrive';
import PolishTracker from './components/PolishTracker';
import OralB from './components/OralB';
import OralBSettings from './components/OralBSettings';
import HomeDashboard from './components/HomeAssistant/HomeDashboard';
import RoomDesigner from './components/HomeAssistant/RoomDesigner';
import HAAutomations from './components/HomeAssistant/HAAutomations';
import HABackups from './components/HomeAssistant/HABackups';
import ServicesOverview from './components/ServicesOverview';
import DebianSysmon from './components/DebianSysmon';
import MaintenanceContainers from './components/MaintenanceContainers';
import NFSMounts from './components/NFSMounts';
import UniFi from './components/UniFi';
import Tax from './components/Tax';
import NginxProxyManager from './components/NginxProxyManager';

// Import modern theme
import theme from './theme';

// Import shared styles
import { fadeInUp, GlassContainer } from './styles/sharedStyles';

const TabLogo = ({ src, alt, size = { xs: 22, sm: 28 }, filter }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: size,
      height: size,
      objectFit: 'contain',
      filter
    }}
  />
);

// Main tab config: each main tab has a list of tab IDs that grant access
const MAIN_TAB_CONFIG = [
  { mainIndex: 0, tabIds: ['immich_statistics', 'immich_adam_library', 'immich_jobs', 'immich_users', 'immich_libraries', 'immich_nightly_tasks', 'immich_about'], label: 'Immich' },
  { mainIndex: 1, tabIds: ['jellyfin'], label: 'Jellyfin' },
  { mainIndex: 2, tabIds: ['nas_drive', 'nas_drive_piotrek', 'nas_drive_adam'], label: 'NAS Drive' },
  { mainIndex: 3, tabIds: ['truenas_services', 'truenas_apps', 'truenas_cron_jobs', 'truenas_virtual_machines', 'truenas_storage_pools', 'truenas_data_protection', 'truenas_file_explorer', 'services_truenas', 'services_rpi5', 'debian_sysmon', 'services_ha', 'task_scheduler'], label: 'Servers' },
  { mainIndex: 4, tabIds: ['home_assistant', 'home_assistant_automations', 'home_assistant_room_designer', 'home_assistant_backups'], label: 'Home Assistant' },
  { mainIndex: 5, tabIds: ['docker_maintenance', 'docker_security'], label: 'Docker' },
  { mainIndex: 6, tabIds: ['chat'], label: 'Chat' },
  { mainIndex: 7, tabIds: ['game_server_ping', 'game_disk_whack'], label: 'Games' },
  { mainIndex: 8, tabIds: ['polish_tracker'], label: 'Polish Tracker' },
  { mainIndex: 9, tabIds: ['oralb'], label: 'Oral-B' },
  { mainIndex: 10, tabIds: ['tax'], label: 'Tax' },
  { mainIndex: 11, tabIds: ['nginx_proxy_manager'], label: 'Nginx' },
];

// Sub-tab indices per main tab (for Immich, TrueNAS, Docker)
const IMMICH_SUB_TAB_IDS = ['immich_statistics', 'immich_adam_library', 'immich_jobs', 'immich_users', 'immich_libraries', 'immich_nightly_tasks', 'immich_about'];
const TRUENAS_SUB_TAB_IDS = ['truenas_services', 'truenas_apps', 'truenas_cron_jobs', 'truenas_virtual_machines', 'truenas_storage_pools', 'truenas_data_protection'];
const NAS_DRIVE_SUB_TAB_IDS = ['nas_drive_piotrek', 'nas_drive_adam'];
const DOCKER_SUB_TAB_IDS = ['docker_maintenance', 'docker_security'];
const MAINTENANCE_TAB_IDS = ['docker_maintenance', 'maintenance_logs', 'gui_theme', 'logon_page', 'maintenance_changelog', 'roadmap', 'nfs_mounts'];
const DEBIAN_SUB_TAB_IDS = ['services_rpi5', 'debian_sysmon', 'services_rpi5', 'docker_maintenance', 'nfs_mounts']; // 0=Services, 1=Sysmon, 2=Data Protection, 3=Containers, 4=NFS Mounts
const GAMES_SUB_TAB_IDS = ['game_snake', 'game_server_ping', 'game_disk_whack'];

const IMMICH_USER_MAPPING = {
  'Adam': 'a92fdf0d-6175-4e4b-b484-ddcb571a6102',
  'Piotrek': 'f7bd4e8a-fe90-486d-9f67-040b90baa912',
  'Marysia': '8a867261-151b-4ffe-906c-a45e28633a2f',
  'Teresa Pawlowska': 'ab0dbf5e-f7a3-45d8-bece-6b68fafd90a3',
  'Zuzanna Pawlowska': 'b1d5895b-9a8b-445b-baa9-87d4fbc3dbc7'
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userInfo, setUserInfo] = useState(null); // { username, is_admin, allowed_tabs }
  const [status, setStatus] = useState(null);
  const statusRef = useRef(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  const [cronJobs, setCronJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cronLoading, setCronLoading] = useState(false);
  const [wolLoading, setWolLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeView, setActiveView] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [immichSubTab, setImmichSubTab] = useState(0);
  const [trueNasSubTab, setTrueNasSubTab] = useState(0);
  const [dockerSubTab, setDockerSubTab] = useState(0);
  const [shutdownLoading, setShutdownLoading] = useState(false);
  const [immichSessions, setImmichSessions] = useState([]);
  const [jellyfinSessions, setJellyfinSessions] = useState([]);
  const [appUserStats, setAppUserStats] = useState({ count: 0, users: [] });
  const [chatSubTab, setChatSubTab] = useState(0);
  const [gamesSubTab, setGamesSubTab] = useState(0);
  const [bonusSubTab, setBonusSubTab] = useState(0); // 0 = Chat, 1 = Games (inside Settings > Bonus)
  const [homeAssistantSubTab, setHomeAssistantSubTab] = useState(0);
  const [haRefreshKey, setHaRefreshKey] = useState(0);
  const [oralbSubTab, setOralbSubTab] = useState(0);
  const [serversSubTab, setServersSubTab] = useState(0);
  const [debianSubTab, setDebianSubTab] = useState(0);
  const [haosSubTab, setHaosSubTab] = useState(0);
  const [adguardData, setAdguardData] = useState(null);
  const [nginxSubTab, setNginxSubTab] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // API configuration and persistence
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      setToken(savedToken);
    }
  }, []);

  const { i18n, t } = useTranslation();

  // Fetch current user info (allowed_tabs, sidebar_config) when token is set
  const refetchUserInfo = useCallback(() => {
    if (!token) return;
    axios
      .get(`${API_BASE_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUserInfo(res.data))
      .catch(() => setUserInfo(null));
  }, [token, API_BASE_URL]);

  const handleSidebarConfigChange = useCallback(
    (newSidebarConfig) => {
      if (!token) return;
      axios
        .post(
          `${API_BASE_URL}/api/user/preferences`,
          { language: i18n?.language || 'en', sidebar_config: newSidebarConfig },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then(() => refetchUserInfo())
        .catch(() => {});
    },
    [token, API_BASE_URL, i18n?.language, refetchUserInfo]
  );

  useEffect(() => {
    if (!token) {
      setUserInfo(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setUserInfo(res.data);
      })
      .catch(() => {
        if (!cancelled) setUserInfo(null);
      });
    return () => { cancelled = true; };
  }, [token, API_BASE_URL]);

  // Visible main tabs based on allowed_tabs (default: all if userInfo not loaded)
  const allowedTabs = userInfo?.allowed_tabs ?? null;
  const visibleMainTabs = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return MAIN_TAB_CONFIG;
    return MAIN_TAB_CONFIG.filter((c) =>
      c.tabIds.some((id) => allowedTabs.includes(id))
    );
  }, [allowedTabs]);

  // Visible sub-tab indices for Immich, TrueNAS, Docker (filter by allowed_tabs)
  const visibleImmichSubTabs = useMemo(() => {
    let tabs;
    if (!allowedTabs || allowedTabs.length === 0) {
      tabs = [0, 1, 2, 3, 4, 5, 6];
    } else {
      tabs = IMMICH_SUB_TAB_IDS.map((id, i) => (allowedTabs.includes(id) ? i : -1)).filter((i) => i >= 0);
    }
    // Mobile: only show Statistics (0) and About (6)
    if (isMobile) {
      tabs = tabs.filter(i => i === 0 || i === 6);
    }
    return tabs;
  }, [allowedTabs, isMobile]);
  const visibleTrueNasSubTabs = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return [0, 1, 2, 3, 4, 5];
    return TRUENAS_SUB_TAB_IDS.map((id, i) => (allowedTabs.includes(id) ? i : -1)).filter((i) => i >= 0);
  }, [allowedTabs]);
  const visibleNasDriveSubTabs = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return [0, 1];
    return NAS_DRIVE_SUB_TAB_IDS.map((id, i) => (allowedTabs.includes(id) ? i : -1)).filter((i) => i >= 0);
  }, [allowedTabs]);
  const visibleDockerSubTabs = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return [0, 1];
    const out = [];
    if (MAINTENANCE_TAB_IDS.some(id => allowedTabs.includes(id))) out.push(0);
    if (allowedTabs.includes('docker_security')) out.push(1);
    return out;
  }, [allowedTabs]);

  // Settings tabs: Maintenance (0), Security (1), Bonus (2). Bonus shown when user has chat or games.
  const hasBonusAccess = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return true;
    return allowedTabs.includes('chat') || GAMES_SUB_TAB_IDS.some((id) => allowedTabs.includes(id));
  }, [allowedTabs]);
  const visibleSettingsTabs = useMemo(() => {
    const base = visibleDockerSubTabs.slice();
    if (hasBonusAccess) base.push(2);
    return base;
  }, [visibleDockerSubTabs, hasBonusAccess]);

  const visibleDebianSubTabs = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return [0, 1, 2, 3, 4];
    const out = [];
    if (allowedTabs.includes('services_rpi5')) {
      out.push(0);
      out.push(2);
    }
    if (allowedTabs.includes('debian_sysmon')) out.push(1);
    if (allowedTabs.includes('docker_maintenance')) out.push(3);
    if (allowedTabs.includes('nfs_mounts')) out.push(4);
    return out.sort((a, b) => a - b);
  }, [allowedTabs]);

  const visibleGamesSubTabs = useMemo(() => {
    if (!allowedTabs || allowedTabs.length === 0) return [0, 1, 2];
    return GAMES_SUB_TAB_IDS.map((id, i) => (allowedTabs.includes(id) ? i : -1)).filter((i) => i >= 0);
  }, [allowedTabs]);

  // Keep activeTab in range when visible main tabs change
  useEffect(() => {
    if (visibleMainTabs.length > 0 && activeTab >= visibleMainTabs.length) {
      setActiveTab(visibleMainTabs.length - 1);
    }
  }, [visibleMainTabs.length, activeTab]);

  // Keep debianSubTab in range when visible Debian sub-tabs change (e.g. user has only debian_sysmon)
  useEffect(() => {
    if (visibleDebianSubTabs.length > 0 && !visibleDebianSubTabs.includes(debianSubTab)) {
      setDebianSubTab(visibleDebianSubTabs[0]);
    }
  }, [visibleDebianSubTabs, debianSubTab]);

  // Keep Settings tab (dockerSubTab) in range when visibleSettingsTabs change (e.g. user has only Bonus)
  useEffect(() => {
    if (visibleSettingsTabs.length > 0 && !visibleSettingsTabs.includes(dockerSubTab)) {
      setDockerSubTab(visibleSettingsTabs[0]);
    }
  }, [visibleSettingsTabs, dockerSubTab]);

  const handleImmichSessionStats = useCallback((stats) => {
    // This is called from ImmichStatistics component, but we now fetch sessions globally
  }, []);

  const handleJellyfinSessionCount = useCallback((count) => {
    // This is called from JellyfinStatistics component, but we now fetch sessions globally
  }, []);

  const fetchImmichSessions = useCallback(async () => {
    if (!token) return;
    
    // Skip if TrueNAS is offline
    const isOnline = statusRef.current?.truenas?.is_online;
    if (statusRef.current?.truenas && !isOnline) {
      console.log('⚠️ Skipping Immich session fetch: TrueNAS is offline');
      setImmichSessions([]);
      return;
    }

    try {
      const now = Date.now();
      const userEntries = Object.entries(IMMICH_USER_MAPPING);
      
      // Fetch all user sessions in parallel
      const sessionRequests = userEntries.map(async ([userName, userId]) => {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/immich/users/${userId}/sessions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const sessions = res.data || [];
          return sessions.map(session => ({
            ...session,
            userName,
            type: 'immich'
          }));
        } catch (err) {
          console.error(`Error fetching Immich sessions for ${userId}:`, err);
          return [];
        }
      });

      const allSessionsArrays = await Promise.all(sessionRequests);
      const filteredSessions = allSessionsArrays
        .flat()
        .filter(session => {
          const updatedAt = session?.updatedAt;
          // 10 minutes threshold for online status
          return updatedAt && (now - new Date(updatedAt)) < 10 * 60 * 1000;
        });

      setImmichSessions(filteredSessions);
    } catch (err) {
      console.error('Error fetching Immich sessions:', err);
    }
  }, [API_BASE_URL, token]);

  const fetchJellyfinSessions = useCallback(async () => {
    if (!token) return;
    
    // Skip if TrueNAS is offline
    const isOnline = statusRef.current?.truenas?.is_online;
    if (statusRef.current?.truenas && !isOnline) {
      console.log('⚠️ Skipping Jellyfin session fetch: TrueNAS is offline');
      setJellyfinSessions([]);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/jellyfin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sessions = (res.data?.sessions || []).map(s => ({
        ...s,
        userName: s.UserName,
        client: s.Client,
        deviceName: s.DeviceName,
        type: 'jellyfin'
      }));
      setJellyfinSessions(sessions);
    } catch (err) {
      console.error('Error fetching Jellyfin sessions:', err);
    }
  }, [API_BASE_URL, token]);

  const fetchAppUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/app-users?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAppUserStats({
        count: response.data.count ?? 0,
        users: response.data.users || [],
      });
    } catch (err) {
      console.error('Error fetching app user activity:', err.message);
    }
  }, [API_BASE_URL, token]);

  const handleLogin = useCallback((newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('token');
    setStatus(null);
    setCronJobs([]);
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      // Fetch status data with cache buster
      const response = await axios.get(`${API_BASE_URL}/api/status?t=${Date.now()}`, {
        timeout: 10000, // Reduced from 15s
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching status:', err.message);
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        // If it's a timeout or network error, set offline status manually
        setStatus(prev => ({
          ...prev,
          truenas: { ...prev?.truenas, is_online: false }
        }));
      }
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE_URL, handleLogout]);

  // Fetch only utilization data (CPU, RAM, Network) - lightweight endpoint
  const fetchUtilization = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/utilization?t=${Date.now()}`, {
        timeout: 10000,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Merge utilization data into existing status
      setStatus(prevStatus => {
        if (!prevStatus) return response.data;
        return {
          ...prevStatus,
          truenas: {
            ...prevStatus.truenas,
            ...response.data.truenas
          }
        };
      });
    } catch (err) {
      console.error('Error fetching utilization:', err.message);
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  }, [token, API_BASE_URL, handleLogout]);

  const fetchCronJobs = useCallback(async () => {
    if (!token) return;
    
    // Skip if TrueNAS is offline
    const isOnline = statusRef.current?.truenas?.is_online;
    if (statusRef.current?.truenas && !isOnline) {
      console.log('⚠️ Skipping Cron Jobs fetch: TrueNAS is offline');
      setCronJobs([]);
      return;
    }

    try {
      setCronLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/cron-jobs?t=${Date.now()}`, {
        timeout: 15000,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      setCronJobs(response.data.cron_jobs || []);
    } catch (err) {
      console.error('Error fetching cron jobs:', err.message);
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setCronJobs([]);
      }
    } finally {
      setCronLoading(false);
    }
  }, [token, API_BASE_URL, handleLogout]);

  const updateCronJobEnabled = useCallback((jobId, enabled) => {
    setCronJobs(prevJobs =>
      prevJobs.map(job =>
        job.id === jobId ? { ...job, enabled } : job
      )
    );
  }, []);

  const updateCronJobSchedule = useCallback((jobId, hour, minute) => {
    setCronJobs(prevJobs =>
      prevJobs.map(job =>
        job.id === jobId
          ? { ...job, schedule: { ...job.schedule, hour, minute } }
          : job
      )
    );
  }, []);

  const sendWakeOnLan = useCallback(async () => {
    try {
      setWolLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/wake-on-lan`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setError(null);
        // Auto-refresh status after WOL to show if it worked
        setTimeout(fetchStatus, 10000);
      }
    } catch (err) {
      setError(`Wake-on-LAN failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setWolLoading(false);
    }
  }, [token, API_BASE_URL, fetchStatus]);

  const shutdownServer = useCallback(async () => {
    try {
      setShutdownLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/shutdown`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setError(null);
        // Auto-refresh status after shutdown
        setTimeout(fetchStatus, 5000);
      }
    } catch (err) {
      setError(`Shutdown failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setShutdownLoading(false);
    }
  }, [token, API_BASE_URL, fetchStatus]);

  const fetchAdguardData = useCallback(async () => {
    if (!token) return;
    try {
      const [statusRes, querylogRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/adguard/status?t=${Date.now()}`, {
          timeout: 10000,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/adguard/querylog?limit=20&t=${Date.now()}`, {
          timeout: 10000,
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      const rawQuerylog = querylogRes.data?.data || querylogRes.data || [];
      const querylog = Array.isArray(rawQuerylog) ? rawQuerylog : [];
      setAdguardData({
        ...(statusRes.data || {}),
        querylog,
      });
    } catch (err) {
      console.error('Error fetching AdGuard data:', err.message);
      setAdguardData(prev => prev ? { ...prev, online: false } : { online: false, protection_enabled: false, num_dns_queries: 0, num_blocked_filtering: 0, blocked_percentage: 0, querylog: [] });
    }
  }, [token, API_BASE_URL]);

  const handleGlobalRefresh = useCallback(() => {
    fetchStatus();
    
    // Derived value based on current ref to avoid dependency loop
    const isOffline = statusRef.current && statusRef.current.truenas && !statusRef.current.truenas.is_online;
    
    fetchAppUsers();
    fetchAdguardData();
    
    // Only fetch these if TrueNAS is likely online
    if (!isOffline) {
      fetchCronJobs();
      fetchImmichSessions();
      fetchJellyfinSessions();
    } else {
      console.log('🔄 Offline mode: skipping TrueNAS-dependent services');
      setCronJobs([]);
      setImmichSessions([]);
      setJellyfinSessions([]);
    }
  }, [fetchStatus, fetchCronJobs, fetchAppUsers, fetchImmichSessions, fetchJellyfinSessions, fetchAdguardData]);

  // Fetch Rpi stats more frequently (every 5 seconds)
  const fetchRpiStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/rpi-stats?t=${Date.now()}`, {
        timeout: 5000,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      setStatus(prevStatus => {
        if (!prevStatus) return { rpi: response.data.rpi };
        return {
          ...prevStatus,
          rpi: response.data.rpi
        };
      });
    } catch (err) {
      console.error('Error fetching Rpi stats:', err.message);
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  }, [token, API_BASE_URL, handleLogout]);

  // Consolidate all refresh logic into a single interval-based effect
  useEffect(() => {
    if (!token) return;

    // Initial refresh
    handleGlobalRefresh();

    // Set up 30s interval for all data
    const globalInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing all data...');
      handleGlobalRefresh();
    }, 30000);

    // Set up 5s interval for RPi stats
    const rpiInterval = setInterval(() => {
      fetchRpiStats();
    }, 5000);

    return () => {
      clearInterval(globalInterval);
      clearInterval(rpiInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Handle immediate refresh when transitioning from offline to online
  const prevOnlineRef = useRef(null);
  useEffect(() => {
    const isOnline = status?.truenas?.is_online;
    // Only trigger if we definitely know it was offline and now it's online
    if (prevOnlineRef.current === false && isOnline === true) {
      console.log('🚀 TrueNAS came back online! Triggering immediate refresh...');
      // Use a small delay to allow status state to settle and avoid immediate re-trigger
      const timer = setTimeout(() => {
        handleGlobalRefresh();
      }, 500);
      return () => clearTimeout(timer);
    }
    if (isOnline !== undefined) {
      prevOnlineRef.current = isOnline;
    }
  }, [status?.truenas?.is_online, handleGlobalRefresh]);

  const connectionStats = useMemo(() => ({
    immich: immichSessions.length,
    jellyfin: jellyfinSessions.length,
    appUsers: appUserStats.count,
    immichSessions,
    jellyfinSessions,
    appUsersList: appUserStats.users
  }), [immichSessions, jellyfinSessions, appUserStats]);

  const apiStatus = useMemo(() => {
    if (!status) return null;
    return {
      truenas: status.truenas?.is_online ?? false,
      immich: status.services?.find(s => s.name === 'immich')?.is_online ?? false,
      jellyfin: status.services?.find(s => s.name === 'jellyfin')?.is_online ?? false,
      ha: status.services?.find(s => s.name === 'home_assistant')?.is_online ?? false,
      adguard: status.services?.find(s => s.name === 'adguard')?.is_online ?? adguardData?.online ?? false,
    };
  }, [status, adguardData]);

  const rpiStatus = useMemo(() => status?.rpi ?? null, [status]);

  if (!token) {
    return (
      <>
        <AnimatedBackground />
        <LoginForm onLogin={handleLogin} apiUrl={API_BASE_URL} />
      </>
    );
  }

  // Check if TrueNAS is offline
  const isTrueNASOffline = status && status.truenas && !status.truenas.is_online;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AnimatedBackground />
      <Sidebar 
        activeView={activeView}
        onViewChange={setActiveView}
        userInfo={userInfo}
        onLogout={handleLogout}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        isTrueNASOffline={isTrueNASOffline}
        sidebarConfig={userInfo?.sidebar_config}
        onSidebarConfigChange={handleSidebarConfigChange}
        apiBaseUrl={API_BASE_URL}
        token={token}
        onUserProfileSave={refetchUserInfo}
        language={i18n?.language || 'en'}
      />
      
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Header
          onRefresh={handleGlobalRefresh}
          onLogout={handleLogout}
          loading={loading}
          connectionStats={connectionStats}
          apiStatus={apiStatus}
          rpiStatus={rpiStatus}
          onMenuClick={() => setMobileOpen(true)}
          token={token}
          onViewChange={setActiveView}
          onServersSubTabChange={setServersSubTab}
          onDebianSubTabChange={setDebianSubTab}
          status={status}
          adguardData={adguardData}
        />
        
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, position: 'relative', zIndex: 1, flexGrow: 1 }}>
          
          {/* Show offline card in specific tabs when TrueNAS is down */}
          {isTrueNASOffline && ['dashboard', 'immich', 'jellyfin', 'nasdrive'].includes(activeView) && (
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
               <TrueNASOfflineCard
                 onWakeOnLan={sendWakeOnLan}
                 wolLoading={wolLoading}
                 onRetry={fetchStatus}
                 apiBaseUrl={API_BASE_URL}
                 token={token}
                 wolEnabled={userInfo?.wol_enabled ?? true}
               />
            </Box>
          )}

          {/* Show regular content for tabs not affected by TrueNAS offline status */}
          {(!isTrueNASOffline || !['dashboard', 'immich', 'jellyfin', 'nasdrive'].includes(activeView)) && (
            <>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '16px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 68, 68, 0.3)',
                    backgroundColor: 'rgba(255, 68, 68, 0.05)',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '& .MuiAlert-message': {
                      color: '#ff8888'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* View Content */}
              <Box sx={{ animation: `${fadeInUp} 0.4s ease-in-out` }}>
                {activeView === 'dashboard' && (
                  <ErrorBoundary>
                    <StatusCard
                      status={status}
                      loading={loading}
                      error={error}
                      onShutdown={shutdownServer}
                      shutdownLoading={shutdownLoading}
                      onRefresh={fetchUtilization}
                      apiBaseUrl={API_BASE_URL}
                      token={token}
                    />
                  </ErrorBoundary>
                )}

                {activeView === 'immich' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '16px',
                              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(255, 64, 129, 0.15))',
                              border: '1px solid rgba(0, 212, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src="/immich.png"
                              alt="Immich"
                              sx={{ width: 36, height: 36, objectFit: 'contain' }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
                              Immich
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <OpenInNew sx={{ fontSize: 14 }} />{' '}
                              <Link
                                href="https://im.raidnet.pl/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#00d4ff' } }}
                              >
                                https://im.raidnet.pl/
                              </Link>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                      <Tabs
                        value={visibleImmichSubTabs.includes(immichSubTab) ? visibleImmichSubTabs.indexOf(immichSubTab) : 0}
                        onChange={(event, newValue) => setImmichSubTab(visibleImmichSubTabs[newValue] ?? 0)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          '& .MuiTabs-indicator': {
                            background: (theme) => theme.palette.primary.main,
                            height: '3px'
                          },
                          '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.6)',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                            '& svg': {
                              color: 'inherit'
                            }
                          }
                        }}
                      >
                        {visibleImmichSubTabs.includes(0) && (
                          <Tab icon={<BarChart3 size={18} />} iconPosition="start" label="Statistics" />
                        )}
                        {visibleImmichSubTabs.includes(1) && (
                          <Tab icon={<Folder size={18} />} iconPosition="start" label="Adam Library" />
                        )}
                        {visibleImmichSubTabs.includes(2) && (
                          <Tab icon={<Briefcase size={18} />} iconPosition="start" label="Jobs" />
                        )}
                        {visibleImmichSubTabs.includes(3) && (
                          <Tab icon={<Users size={18} />} iconPosition="start" label="Users" />
                        )}
                        {visibleImmichSubTabs.includes(4) && (
                          <Tab icon={<Library size={18} />} iconPosition="start" label="Libraries" />
                        )}
                        {visibleImmichSubTabs.includes(5) && (
                          <Tab icon={<Clock size={18} />} iconPosition="start" label="Nightly Tasks" />
                        )}
                        {visibleImmichSubTabs.includes(6) && (
                          <Tab icon={<InfoIcon size={18} />} iconPosition="start" label="About" />
                        )}
                      </Tabs>
                    </Box>
                    <Box>
                      {immichSubTab === 0 && (
                        <ErrorBoundary>
                          <ImmichStatistics
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                            onSessionStats={handleImmichSessionStats}
                            isTrueNASOffline={isTrueNASOffline}
                          />
                        </ErrorBoundary>
                      )}
                      {immichSubTab === 1 && (
                        <ErrorBoundary>
                          <FileExplorer 
                            apiBaseUrl={API_BASE_URL} 
                            token={token} 
                          />
                        </ErrorBoundary>
                      )}
                      {immichSubTab === 2 && (
                        <ErrorBoundary>
                          <ImmichJobs
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                          />
                        </ErrorBoundary>
                      )}
                      {immichSubTab === 3 && (
                        <ErrorBoundary>
                          <ImmichUsers
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                          />
                        </ErrorBoundary>
                      )}
                      {immichSubTab === 4 && (
                        <ErrorBoundary>
                          <ImmichLibraries
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                          />
                        </ErrorBoundary>
                      )}
                      {immichSubTab === 5 && (
                        <ErrorBoundary>
                          <ImmichNightlyTasks
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                          />
                        </ErrorBoundary>
                      )}
                      {immichSubTab === 6 && (
                        <ErrorBoundary>
                          <ImmichAbout
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                          />
                        </ErrorBoundary>
                      )}
                    </Box>
                  </GlassContainer>
                )}

                {activeView === 'jellyfin' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <ErrorBoundary>
                      <JellyfinStatistics
                        apiBaseUrl={API_BASE_URL}
                        token={token}
                        jellyfinUrl="https://jelly.raidnet.pl/"
                        onSessionsCountChange={handleJellyfinSessionCount}
                        isTrueNASOffline={isTrueNASOffline}
                      />
                    </ErrorBoundary>
                  </GlassContainer>
                )}

                {activeView === 'polish_tracker' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <ErrorBoundary>
                      <PolishTracker 
                        apiBaseUrl={API_BASE_URL} 
                        token={token} 
                      />
                    </ErrorBoundary>
                  </GlassContainer>
                )}

                {activeView === 'oralb' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                      <Tabs
                        value={oralbSubTab}
                        onChange={(e, v) => setOralbSubTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          '& .MuiTabs-indicator': { background: (t) => t.palette.primary.main, height: '3px' },
                          '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.6)',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&.Mui-selected': { color: (t) => t.palette.primary.main },
                          },
                        }}
                      >
                        <Tab icon={<LayoutDashboard size={18} />} iconPosition="start" label="Dashboard" />
                        <Tab icon={<Settings size={18} />} iconPosition="start" label="Settings" />
                      </Tabs>
                    </Box>
                    <Box>
                      {oralbSubTab === 0 && (
                        <ErrorBoundary>
                          <OralB apiBaseUrl={API_BASE_URL} token={token} />
                        </ErrorBoundary>
                      )}
                      {oralbSubTab === 1 && (
                        <ErrorBoundary>
                          <OralBSettings apiBaseUrl={API_BASE_URL} token={token} />
                        </ErrorBoundary>
                      )}
                    </Box>
                  </GlassContainer>
                )}

                {activeView === 'nasdrive' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <ErrorBoundary>
                      <NASDrive 
                        apiBaseUrl={API_BASE_URL} 
                        token={token}
                        allowedTabs={allowedTabs}
                        visibleSubTabs={visibleNasDriveSubTabs}
                      />
                    </ErrorBoundary>
                  </GlassContainer>
                )}

                {activeView === 'unifi' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <ErrorBoundary>
                      <UniFi 
                        apiBaseUrl={API_BASE_URL} 
                        token={token}
                        allowedTabs={allowedTabs || []}
                        unifiUrl="https://un.raidnet.pl/"
                      />
                    </ErrorBoundary>
                  </GlassContainer>
                )}

                {activeView === 'tax' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <ErrorBoundary>
                      <Tax 
                        apiBaseUrl={API_BASE_URL} 
                        token={token}
                      />
                    </ErrorBoundary>
                  </GlassContainer>
                )}

                {activeView === 'nginx' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <ErrorBoundary>
                      <NginxProxyManager 
                        apiBaseUrl={API_BASE_URL} 
                        token={token}
                      />
                    </ErrorBoundary>
                  </GlassContainer>
                )}

                {activeView === 'servers' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                      <Tabs
                        value={serversSubTab}
                        onChange={(event, newValue) => setServersSubTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          '& .MuiTabs-indicator': {
                            background: (theme) => theme.palette.primary.main,
                            height: '3px'
                          },
                          '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.6)',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                            '& svg': { color: 'inherit' }
                          }
                        }}
                      >
                        <Tab icon={<TrueNASIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="TrueNAS" />
                        <Tab icon={<Cpu size={18} />} iconPosition="start" label="Debian 13" />
                        <Tab icon={<Home size={18} />} iconPosition="start" label="HAOS" />
                        <Tab icon={<Clock size={18} />} iconPosition="start" label="Task Scheduler" />
                      </Tabs>
                    </Box>

                    <Box>
                      {serversSubTab === 0 && (
                        <>
                          {isTrueNASOffline ? (
                             <TrueNASOfflineCard
                               onWakeOnLan={sendWakeOnLan}
                               wolLoading={wolLoading}
                               onRetry={fetchStatus}
                               apiBaseUrl={API_BASE_URL}
                               token={token}
                               wolEnabled={userInfo?.wol_enabled ?? true}
                             />
                          ) : (
                            <>
                              <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                                <Tabs
                                  value={visibleTrueNasSubTabs.includes(trueNasSubTab) ? visibleTrueNasSubTabs.indexOf(trueNasSubTab) : 0}
                                  onChange={(event, newValue) => setTrueNasSubTab(visibleTrueNasSubTabs[newValue] ?? 0)}
                                  variant="scrollable"
                                  scrollButtons="auto"
                                  sx={{
                                    '& .MuiTabs-indicator': {
                                      background: (theme) => theme.palette.primary.main,
                                      height: '3px'
                                    },
                                    '& .MuiTab-root': {
                                      color: 'rgba(255, 255, 255, 0.6)',
                                      textTransform: 'none',
                                      fontSize: '0.9rem',
                                      fontWeight: 600,
                                      minHeight: '48px',
                                      '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                                      '& svg': { color: 'inherit' }
                                    }
                                  }}
                                >
                                  {visibleTrueNasSubTabs.includes(0) && <Tab icon={<ServerIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Services Monitoring" />}
                                  {visibleTrueNasSubTabs.includes(1) && <Tab icon={<Grid3X3 size={18} />} iconPosition="start" label="App Control" />}
                                  {visibleTrueNasSubTabs.includes(2) && <Tab icon={<Clock size={18} />} iconPosition="start" label="Cron Jobs" />}
                                  {visibleTrueNasSubTabs.includes(3) && <Tab icon={<Cpu size={18} />} iconPosition="start" label="Virtual Machines" />}
                                  {visibleTrueNasSubTabs.includes(4) && <Tab icon={<HardDrive size={18} />} iconPosition="start" label="Storage Pools" />}
                                  {visibleTrueNasSubTabs.includes(5) && <Tab icon={<Shield size={18} />} iconPosition="start" label="Data Protection" />}
                                </Tabs>
                              </Box>
                              <Box>
                                {trueNasSubTab === 0 && (
                                  <ErrorBoundary>
                                    <ServicesOverview 
                                      status={status}
                                      loading={loading}
                                      token={token}
                                      onlyTrueNAS={true}
                                    />
                                  </ErrorBoundary>
                                )}
                                {trueNasSubTab === 1 && (
                                  <ErrorBoundary>
                                    <AppsManagement 
                                      apiBaseUrl={API_BASE_URL} 
                                      token={token}
                                      onlyTrueNAS={true}
                                    />
                                  </ErrorBoundary>
                                )}
                                {trueNasSubTab === 2 && (
                                  <ErrorBoundary>
                                    {cronLoading ? (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress sx={{ color: '#00d4ff' }} />
                                      </Box>
                                    ) : cronJobs.length > 0 ? (
                                      <Grid container spacing={3}>
                                        {cronJobs.map((job, index) => (
                                          <Grid item xs={12} sm={6} lg={4} xl={3} key={job.id}>
                                            <Box
                                              sx={{
                                                animation: `${fadeInUp} 0.6s ease-out`,
                                                animationDelay: `${index * 0.1}s`,
                                                animationFillMode: 'both'
                                              }}
                                            >
                                              <CronJobCard
                                                job={job}
                                                onUpdateEnabled={updateCronJobEnabled}
                                                onUpdateSchedule={updateCronJobSchedule}
                                                apiBaseUrl={API_BASE_URL}
                                                token={token}
                                              />
                                            </Box>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    ) : (
                                      <Box sx={{ textAlign: 'center', py: 6, color: 'rgba(255, 255, 255, 0.7)' }}>
                                        <Typography variant="body1">
                                          {cronLoading ? 'Loading cron jobs...' : 'No cron jobs found or unable to connect to TrueNAS API'}
                                        </Typography>
                                      </Box>
                                    )}
                                  </ErrorBoundary>
                                )}
                                {trueNasSubTab === 3 && (
                                  <ErrorBoundary>
                                    <VirtualMachines 
                                      apiBaseUrl={API_BASE_URL} 
                                      token={token} 
                                    />
                                  </ErrorBoundary>
                                )}
                                {trueNasSubTab === 4 && (
                                  <ErrorBoundary>
                                    <StoragePools 
                                      apiBaseUrl={API_BASE_URL} 
                                      token={token} 
                                    />
                                  </ErrorBoundary>
                                )}
                                {trueNasSubTab === 5 && (
                                  <ErrorBoundary>
                                    <DataProtection 
                                      apiBaseUrl={API_BASE_URL} 
                                      token={token} 
                                      onlyTrueNAS={true}
                                    />
                                  </ErrorBoundary>
                                )}
                              </Box>
                            </>
                          )}
                        </>
                      )}

                      {serversSubTab === 1 && (
                        <>
                          <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                            <Tabs
                              value={visibleDebianSubTabs.includes(debianSubTab) ? visibleDebianSubTabs.indexOf(debianSubTab) : 0}
                              onChange={(event, newValue) => setDebianSubTab(visibleDebianSubTabs[newValue] ?? 0)}
                              variant="scrollable"
                              scrollButtons="auto"
                              sx={{
                                '& .MuiTabs-indicator': {
                                  background: (theme) => theme.palette.primary.main,
                                  height: '3px'
                                },
                                '& .MuiTab-root': {
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  textTransform: 'none',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                  minHeight: '48px',
                                  '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                                  '& svg': { color: 'inherit' }
                                }
                              }}
                            >
                              {visibleDebianSubTabs.includes(0) && <Tab icon={<ServerIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Services Monitoring" />}
                              {visibleDebianSubTabs.includes(1) && <Tab icon={<Activity size={18} />} iconPosition="start" label="Sysmon" />}
                              {visibleDebianSubTabs.includes(2) && <Tab icon={<Shield size={18} />} iconPosition="start" label="Data Protection" />}
                              {visibleDebianSubTabs.includes(3) && <Tab icon={<CloudDownloadIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Containers" />}
                              {visibleDebianSubTabs.includes(4) && <Tab icon={<HardDrive size={18} />} iconPosition="start" label="NFS Mounts" />}
                            </Tabs>
                          </Box>
                          <Box>
                            {debianSubTab === 0 && (
                              <ErrorBoundary>
                                <ServicesOverview 
                                  status={status}
                                  loading={loading}
                                  token={token}
                                  onlyRpi={true}
                                />
                              </ErrorBoundary>
                            )}
                            {debianSubTab === 1 && (
                              <ErrorBoundary>
                                <DebianSysmon rpiStatus={rpiStatus} loading={loading} />
                              </ErrorBoundary>
                            )}
                            {debianSubTab === 2 && (
                              <ErrorBoundary>
                                <DataProtection 
                                  apiBaseUrl={API_BASE_URL} 
                                  token={token} 
                                  onlyRpi={true}
                                />
                              </ErrorBoundary>
                            )}
                            {debianSubTab === 3 && (
                              <ErrorBoundary>
                                <MaintenanceContainers 
                                  apiBaseUrl={API_BASE_URL} 
                                  token={token}
                                />
                              </ErrorBoundary>
                            )}
                            {debianSubTab === 4 && (
                              <ErrorBoundary>
                                <NFSMounts 
                                  apiBaseUrl={API_BASE_URL} 
                                  token={token}
                                />
                              </ErrorBoundary>
                            )}
                          </Box>
                        </>
                      )}

                      {serversSubTab === 2 && (
                        <>
                          <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                            <Tabs
                              value={haosSubTab}
                              onChange={(event, newValue) => setHaosSubTab(newValue)}
                              variant="scrollable"
                              scrollButtons="auto"
                              sx={{
                                '& .MuiTabs-indicator': {
                                  background: (theme) => theme.palette.primary.main,
                                  height: '3px'
                                },
                                '& .MuiTab-root': {
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  textTransform: 'none',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                  minHeight: '48px',
                                  '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                                  '& svg': { color: 'inherit' }
                                }
                              }}
                            >
                              <Tab icon={<ServerIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Services Monitoring" />
                              <Tab icon={<Shield size={18} />} iconPosition="start" label="Data Protection" />
                            </Tabs>
                          </Box>
                          <Box>
                            {haosSubTab === 0 && (
                              <ErrorBoundary>
                                <ServicesOverview 
                                  status={status}
                                  loading={loading}
                                  token={token}
                                  onlyHA={true}
                                />
                              </ErrorBoundary>
                            )}
                            {haosSubTab === 1 && (
                              <ErrorBoundary>
                                <HABackups 
                                  apiBaseUrl={API_BASE_URL} 
                                  token={token} 
                                />
                              </ErrorBoundary>
                            )}
                          </Box>
                        </>
                      )}

                      {serversSubTab === 3 && (
                        <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                          <ErrorBoundary>
                            <TaskScheduler 
                              apiBaseUrl={API_BASE_URL} 
                              token={token}
                            />
                          </ErrorBoundary>
                        </GlassContainer>
                      )}
                    </Box>
                  </GlassContainer>
                )}

                {activeView === 'home_assistant' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '16px',
                              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(255, 64, 129, 0.15))',
                              border: '1px solid rgba(0, 212, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src="/home-assistant-alt.png"
                              alt="Home Assistant"
                              sx={{ width: 36, height: 36, objectFit: 'contain' }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
                              Home Assistant
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <OpenInNew sx={{ fontSize: 14 }} />{' '}
                              <Link
                                href="https://ha.raidnet.pl/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#00d4ff' } }}
                              >
                                https://ha.raidnet.pl/
                              </Link>
                            </Typography>
                          </Box>
                        </Box>
                        <Tooltip title="Refresh data">
                          <IconButton
                            onClick={() => setHaRefreshKey(k => k + 1)}
                            sx={{
                              color: '#00d4ff',
                              background: 'rgba(0, 212, 255, 0.1)',
                              border: '1px solid rgba(0, 212, 255, 0.1)',
                              '&:hover': { background: 'rgba(0, 212, 255, 0.2)', borderColor: 'rgba(0, 212, 255, 0.3)' },
                            }}
                          >
                            <Refresh />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                      <Tabs
                        value={homeAssistantSubTab}
                        onChange={(event, newValue) => setHomeAssistantSubTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          '& .MuiTabs-indicator': {
                            background: (theme) => theme.palette.primary.main,
                            height: '3px'
                          },
                          '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.6)',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                            '& svg': {
                              color: 'inherit'
                            }
                          }
                        }}
                      >
                        <Tab icon={<LayoutDashboard size={18} />} iconPosition="start" label="Dashboard" />
                        <Tab icon={<Zap size={18} />} iconPosition="start" label="Automations" />
                        <Tab icon={<PencilRuler size={18} />} iconPosition="start" label="Room Designer" />
                      </Tabs>
                    </Box>
                    <Box>
                      {homeAssistantSubTab === 0 && (
                        <ErrorBoundary>
                          <HomeDashboard key={haRefreshKey} apiBaseUrl={API_BASE_URL} token={token} />
                        </ErrorBoundary>
                      )}
                      {homeAssistantSubTab === 1 && (
                        <ErrorBoundary>
                          <HAAutomations key={haRefreshKey} apiBaseUrl={API_BASE_URL} token={token} />
                        </ErrorBoundary>
                      )}
                      {homeAssistantSubTab === 2 && (
                        <ErrorBoundary>
                          {allowedTabs.includes('home_assistant_room_designer') || userInfo?.is_admin ? (
                            <RoomDesigner key={haRefreshKey} apiBaseUrl={API_BASE_URL} token={token} />
                          ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Access Denied: Room Designer is not enabled for your account.
                              </Typography>
                            </Box>
                          )}
                        </ErrorBoundary>
                      )}
                    </Box>
                  </GlassContainer>
                )}

                {activeView === 'settings' && (
                  <GlassContainer sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                      <Tabs
                        value={visibleSettingsTabs.includes(dockerSubTab) ? visibleSettingsTabs.indexOf(dockerSubTab) : 0}
                        onChange={(event, newValue) => setDockerSubTab(visibleSettingsTabs[newValue] ?? 0)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          '& .MuiTabs-indicator': {
                            background: (theme) => theme.palette.primary.main,
                            height: '3px'
                          },
                          '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.6)',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                            '& svg': {
                              color: 'inherit'
                            }
                          }
                        }}
                      >
                        {visibleSettingsTabs.includes(0) && <Tab label={t('settings.maintenance')} />}
                        {visibleSettingsTabs.includes(1) && <Tab label={t('settings.security')} />}
                        {visibleSettingsTabs.includes(2) && <Tab label={t('settings.bonus')} />}
                      </Tabs>
                    </Box>
                    <Box>
                      {dockerSubTab === 0 && (
                        <ErrorBoundary>
                          <Maintenance 
                            apiBaseUrl={API_BASE_URL} 
                            token={token}
                            userInfo={userInfo}
                            onSave={refetchUserInfo}
                            isTrueNASOffline={isTrueNASOffline}
                          />
                        </ErrorBoundary>
                      )}
                      {dockerSubTab === 1 && (
                        <ErrorBoundary>
                          <SecuritySettings 
                            apiBaseUrl={API_BASE_URL} 
                            token={token}
                            userInfo={userInfo}
                          />
                        </ErrorBoundary>
                      )}
                      {dockerSubTab === 2 && (
                        <>
                          <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3, mt: 2 }}>
                            <Tabs
                              value={bonusSubTab}
                              onChange={(e, v) => setBonusSubTab(v)}
                              variant="scrollable"
                              scrollButtons="auto"
                              sx={{
                                '& .MuiTabs-indicator': { background: (t) => t.palette.primary.main, height: '3px' },
                                '& .MuiTab-root': {
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  textTransform: 'none',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  '&.Mui-selected': { color: (t) => t.palette.primary.main },
                                },
                              }}
                            >
                              <Tab icon={<ChatIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('settings.chat')} />
                              <Tab icon={<GamesIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('settings.games')} />
                            </Tabs>
                          </Box>
                          <Box>
                            {bonusSubTab === 0 && (
                              <ErrorBoundary>
                                <Chat apiBaseUrl={API_BASE_URL} token={token} userInfo={userInfo} />
                              </ErrorBoundary>
                            )}
                            {bonusSubTab === 1 && (
                              <>
                                <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
                                  <Tabs
                                    value={visibleGamesSubTabs.includes(gamesSubTab) ? visibleGamesSubTabs.indexOf(gamesSubTab) : 0}
                                    onChange={(event, newValue) => setGamesSubTab(visibleGamesSubTabs[newValue] ?? 0)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                      '& .MuiTabs-indicator': { background: (t) => t.palette.primary.main, height: '3px' },
                                      '& .MuiTab-root': {
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        '&.Mui-selected': { color: (t) => t.palette.primary.main },
                                        '& svg': { color: 'inherit' },
                                      },
                                    }}
                                  >
                                    {visibleGamesSubTabs.includes(0) && <Tab icon={<Gamepad2 size={18} />} iconPosition="start" label={t('settings.iconSnake')} />}
                                    {visibleGamesSubTabs.includes(1) && <Tab icon={<HardDrive size={18} />} iconPosition="start" label={t('settings.pingDefender')} />}
                                    {visibleGamesSubTabs.includes(2) && <Tab icon={<HardDrive size={18} />} iconPosition="start" label={t('settings.diskWhack')} />}
                                  </Tabs>
                                </Box>
                                <Box>
                                  {gamesSubTab === 0 && (
                                    <ErrorBoundary><SnakeGame /></ErrorBoundary>
                                  )}
                                  {gamesSubTab === 1 && (
                                    <ErrorBoundary><ServerPingDefender /></ErrorBoundary>
                                  )}
                                  {gamesSubTab === 2 && (
                                    <ErrorBoundary><DiskWhackAMole /></ErrorBoundary>
                                  )}
                                </Box>
                              </>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  </GlassContainer>
                )}

                {activeView === 'documentation' && (
                  <Documentation />
                )}
              </Box>
            </>
          )}
        </Container>
        <Footer onNavigate={setActiveView} />
      </Box>
    </Box>
  );
}

export default App;
